import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import Login from './Auth/Login';
import Signup from './Auth/Signup';
import ForgotPassword from './Auth/ForgotPassword';
import ResetPassword from './Auth/ResetPassword';
import Energy from './Energy';
import Observability from './Observability';
import Growth from './Growth/Growth';
import System from './System/System';
import Home from './Home';
import Copilot from './Copilot';
import Profile from './Profile';
import Settings from './Settings';
import Landing from './Landing';
import { useTheme } from './hooks/useTheme';
import ModulePage from './components/ModulePage';
import WaitlistPage from './Waitlist/WaitlistPage';
import WaitlistConfirmation from './Waitlist/WaitlistConfirmation';
import './Waitlist/Waitlist.css';
import './App.css';

/* Legacy ParticleCanvas removed in Phase 5 — see src/Landing/_archive/. */

/* ============================================================
   ROUTE REDIRECTS — Phase 1 path migration.
   Old → new mapping. Performed client-side; Netlify also serves
   /index.html for any path so this catches every legacy link.
   ============================================================ */
const REDIRECTS = {
  '/skill-pulse': '/growth',
  '/skill-pulse/interview': '/growth/interview',
  '/skill-pulse/garden': '/growth/garden',
  '/skill-pulse/mirror': '/growth/mirror',
  '/skill-pulse/copilot': '/copilot',
  '/growth/copilot':       '/copilot',
  '/life-admin': '/system',
  '/life-admin/health': '/system/health',
  '/life-admin/money': '/system/money',
  '/life-admin/planner': '/system/planner',
  '/burnout-guard': '/energy',
};

// Stub MODULE_INSIGHTS removed in Phase 3 — every module now owns its
// own real CruxInsight (or, for Copilot, replaces the Insight surface
// entirely). Home still renders stub data; that becomes a real
// cross-module call in a later phase.

/* ============================================================
   APP ROOT
   ============================================================ */
export default function App() {
  // Initialize theme — Phase 12. The inline script in public/index.html
  // already set <html data-theme=…> before React mounted; this hook keeps
  // it in sync with localStorage + OS preference changes for the rest of
  // the session.
  useTheme();

  const [user, setUser] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const path = window.location.pathname;

  // Client-side 301-style redirect for legacy routes.
  // Run before any rendering decisions so the URL is updated immediately.
  useEffect(() => {
    if (REDIRECTS[path]) {
      window.history.replaceState(null, '', REDIRECTS[path]);
      // Force a re-render by reloading; this ensures the new path is honored
      // by the rest of the app's path-based routing.
      window.location.replace(REDIRECTS[path]);
    }
  }, [path]);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setUser(data?.session?.user || null);
      setSessionChecked(true);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setUser(session?.user || null);
    });

    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe?.();
    };
  }, []);

  const handleLogin = (loggedInUser) => {
    setUser(loggedInUser);
    window.location.href = '/';
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    window.location.href = '/';
  };

  if (!sessionChecked) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F7F8FA',
        color: '#6B7280',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        letterSpacing: '0.04em',
        fontSize: '0.85rem',
      }}>
        Loading...
      </div>
    );
  }

  // If we're on a redirect path, render nothing while the redirect fires.
  if (REDIRECTS[path]) return null;

  /* ----- Public routes ----- */

  if (path === '/landing') {
    return <Landing />;
  }

  if (path === '/waitlist/confirmed') {
    return <WaitlistConfirmation />;
  }

  if (path === '/waitlist') {
    return <WaitlistPage />;
  }

  if (path === '/login') {
    if (user) {
      window.location.href = '/';
      return null;
    }
    return <Login onLogin={handleLogin} />;
  }

  if (path === '/signup') {
    if (user) {
      window.location.href = '/';
      return null;
    }
    return <Signup onLogin={handleLogin} />;
  }

  if (path === '/forgot-password') {
    if (user) {
      window.location.href = '/';
      return null;
    }
    return <ForgotPassword />;
  }

  if (path === '/reset-password') {
    // Intentionally NOT redirecting logged-in users away from this route —
    // when a user follows the email link, Supabase auto-creates a recovery
    // session from the URL hash, so `user` is truthy by the time we render.
    // The page itself gates access on a valid recovery/sign-in event.
    return <ResetPassword />;
  }

  /* ----- Protected app routes ----- */

  // If signed in, the new home shell ("/") and module pages render with the
  // sidebar layout. If signed out, we still show the legacy landing at "/"
  // so visitor flow is unbroken.
  const isHome = path === '/' || path === '';
  const isEnergy = path === '/energy';
  const isGrowth = path === '/growth' || path.startsWith('/growth/');
  const isSystem = path === '/system' || path.startsWith('/system/');
  const isCopilot = path === '/copilot';
  const isProfile = path === '/profile';
  const isSettings = path === '/settings';
  const isObservability = path === '/admin/observability';

  const inAppShell = isHome || isEnergy || isGrowth || isSystem || isCopilot || isProfile || isSettings || isObservability;

  if (inAppShell) {
    if (!user) {
      // Public visitors landing on "/" still see marketing; protected
      // module pages bounce to login.
      if (isHome) return <Landing />;
      window.location.href = '/login';
      return null;
    }

    if (isHome) return <Home user={user} onSignOut={handleSignOut} />;

    if (isEnergy) {
      // Energy renders its own CruxInsight (real data) inside the module.
      return (
        <ModulePage
          currentPath="/energy"
          user={user}
          onSignOut={handleSignOut}
        >
          <Energy />
        </ModulePage>
      );
    }

    if (isGrowth) {
      // Growth renders its own CruxInsight (real data) inside the module.
      return (
        <ModulePage
          currentPath="/growth"
          user={user}
          onSignOut={handleSignOut}
        >
          <Growth />
        </ModulePage>
      );
    }

    if (isSystem) {
      // System renders its own CruxInsight (real data) inside the module.
      return (
        <ModulePage
          currentPath="/system"
          user={user}
          onSignOut={handleSignOut}
        >
          <System />
        </ModulePage>
      );
    }

    if (isCopilot) {
      // Copilot IS the insight engine — no stub Insight at top.
      return (
        <ModulePage
          currentPath="/copilot"
          user={user}
          onSignOut={handleSignOut}
        >
          <Copilot />
        </ModulePage>
      );
    }

    if (isProfile) {
      // Profile renders inside the standard sidebar shell.
      return (
        <ModulePage
          currentPath="/profile"
          user={user}
          onSignOut={handleSignOut}
        >
          <Profile />
        </ModulePage>
      );
    }

    if (isSettings) {
      return (
        <ModulePage
          currentPath="/settings"
          user={user}
          onSignOut={handleSignOut}
        >
          <Settings />
        </ModulePage>
      );
    }

    if (isObservability) return <Observability />;
  }

  // Fallback — unknown path. Show landing for visitors, Home for signed in.
  if (user) return <Home user={user} onSignOut={handleSignOut} />;
  return <Landing />;
}
